/* eslint-disable camelcase */
import { Grid, MenuItem, Select } from '@mui/material';
import { withStyles } from '@mui/styles';
import { RoutalDialog, RoutalPalette, RoutalSelect, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Location } from '@nx-smartmonkey/shared/domain';
import { FormArray } from '../components/form-fields/FormArray';
import FormTimeWindows from '../components/form-fields/FormTimeWindows';
import { renderCheckbox, renderInputLocation, renderInputText } from '../components/right-panel-info/Common';
import { Client } from '../domain/client/Client';
import { Driver } from '../domain/driver/Driver';
import { Project } from '../domain/project/Project';
import { Stop } from '../domain/stop/Stop';
import { Vehicle } from '../domain/vehicle/Vehicle';
import { intl } from '../helpers/IntGlobalProvider';
import { useUpdateConstraints } from '../hooks/constraints/useUpdateConstraints';
import { useRetrieveActiveProjectGeoFences } from '../hooks/project/useRetrieveActiveProjectGeoFences';
import { GeoFencesSelector } from '../react-components/GeoFencesSelector';
import Text from '../react-components/Text';

const styles = () => ({
  select: {
    backgroundColor: `#EBF0F8`,
    marginTop: `6px`,
    paddingLeft: `6px`,
    borderRadius: `4px`,
    '&:focus': {
      borderRadius: `4px`,
      backgroundColor: `#FDF6ED`,
    },
  },
});

export type ConstraintsType =
  | `location`
  | `time_window`
  | `time_windows`
  | `number`
  | `text_array`
  | `minutes`
  | `kilometers`
  | `text`
  | `geo_fences`;

type ConstraintsDialogProps = {
  classes: {
    select?: string;
  };
  open: boolean;
  constraintsMap: {
    id: string;
    type: ConstraintsType;
    noText?: string;
    noTextId?: string;
    options?: {
      value?: string;
      label_id?: string;
      text?: string;
      label?: string;
    }[];
  }[];
  project?: Project;
  data?: Driver | Stop | Vehicle | Client;
  numberOfElements: number;
  typeData: `drivers` | `stops` | `vehicles` | `clients`;
  idsToUpdate: string[];
  onCloseDialog: (...args: any[]) => any;
  onUpdateConstraints: (...args: any[]) => any;
};

const modifications = {
  keep: `keep_value`,
  edit: `edit_value`,
  remove: `remove_value`,
};

export const ConstraintsDialog = withStyles(styles)(
  ({
    classes,
    open,
    numberOfElements = 0,
    project,
    data,
    constraintsMap,
    typeData,
    idsToUpdate,
    onCloseDialog,
    onUpdateConstraints,
  }: ConstraintsDialogProps) => {
    const [constraintsFieldsMap, setConstraintsFieldsMap] = useState<Record<string, any>>({});
    const [constraints, setConstraints] = useState<Record<string, any>>({});
    const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();

    const {
      run: updateConstraints,
      success: updateSuccess,
      // error: updateError,
      isLoading: isUpdating,
    } = useUpdateConstraints({ typeData });

    useEffect(() => {
      if (open) {
        setConstraints(data ?? {});
        setConstraintsFieldsMap(
          constraintsMap.reduce(
            (prev, field) => ({ ...prev, [field.id]: { type: field.type, modification: modifications.keep } }),
            {}
          )
        );
      }
    }, [data, open, constraintsMap]);

    useEffect(() => {
      if (updateSuccess) {
        onUpdateConstraints();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateSuccess]);

    let propertiesPrefixType = ``;
    switch (typeData) {
      case `stops`:
        propertiesPrefixType = `properties.service`;
        break;
      case `drivers`:
        propertiesPrefixType = `properties.route`;
        break;
      case `vehicles`:
        propertiesPrefixType = `properties.vehicle`;
        break;
      case `clients`:
        propertiesPrefixType = `properties.client`;
        break;
      default:
        return null;
    }

    const setModificationType = (property: string, action: string) => {
      setConstraintsFieldsMap((prevConstraintsFieldsMap) => ({
        ...prevConstraintsFieldsMap,
        [property]: { ...prevConstraintsFieldsMap[property], modification: action },
      }));
      if (action === modifications.remove) {
        setConstraints((prevConstraints) => ({
          ...prevConstraints,
          [property]: [`time_windows`, `text_array`].includes(constraintsFieldsMap[property].type) ? [] : null,
        }));
      }
    };

    return (
      <RoutalDialog
        open={open}
        onCloseDialog={onCloseDialog}
        loading={isUpdating}
        dialogProps={{
          disableBackdropClick: true,
          disableEscapeKeyDown: true,
          style: {
            minHeight: `400px`,
          },
        }}
        titleContent={<Text variant="h6" intlId="edit_constraints.title" />}
        cancelButtonText={intl.formatMessage({ id: `edit_constraints.buttons.cancel` })}
        confirmButtonText={intl.formatMessage({ id: `edit_constraints.buttons.confirm` })}
        onConfirm={() => {
          updateConstraints({ ids: idsToUpdate, constraints });
        }}
        // eslint-disable-next-line react/jsx-no-useless-fragment
        footerContent={isUpdating ? <></> : undefined}
      >
        {numberOfElements > 0 ? (
          <Text intlId={`${typeData}.edit_constraints_dialog.content`} intlValues={{ data: numberOfElements }} />
        ) : null}

        <VSpacer large />

        <Grid container direction="column">
          {Object.entries(constraintsFieldsMap).map((fieldEntries) => {
            const id = fieldEntries[0];
            const field = fieldEntries[1];

            const elements = [
              <Text
                intlId={`${propertiesPrefixType}.${id}`}
                variant="body"
                style={{ cursor: `default`, color: RoutalPalette.neutral20, marginBottom: `4px` }}
              />,
              <RoutalSelect
                id={`${field.type}${id}-select-modification`}
                style={{
                  ...(field.modification === modifications.remove ? { borderColor: RoutalPalette.danger } : {}),
                }}
                popoverProps={{
                  options: [
                    {
                      key: `keep_value`,
                      onClick: () => {
                        setConstraints(data ? { ...data, [id]: (data as any)[id] } : {});
                        setModificationType(id, `keep_value`);
                      },
                      content: <Text intlId="edit_constraints.modification.keep_value" style={{ cursor: `pointer` }} />,
                    },
                    {
                      key: `edit_value`,
                      onClick: () => {
                        setModificationType(id, `edit_value`);
                      },
                      content: <Text intlId="edit_constraints.modification.edit_value" style={{ cursor: `pointer` }} />,
                    },
                    {
                      key: `remove_value`,
                      onClick: () => {
                        setModificationType(id, `remove_value`);
                      },
                      content: (
                        <Text intlId="edit_constraints.modification.remove_value" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ],
                }}
              >
                <Text
                  style={{ cursor: `pointer`, width: `100%`, paddingLeft: `8px`, textAlign: `start` }}
                  intlId={`edit_constraints.modification.${field.modification}`}
                />
              </RoutalSelect>,
            ];
            if (field.modification === modifications.edit) {
              switch (field.type) {
                case `number`:
                  elements.push(
                    <>
                      <VSpacer small />
                      {renderInputText({
                        variant: `outlined`,
                        property: id,
                        type: `number`,
                        handleUpdate: (property, value) => {
                          setConstraints((prevConstraints) => ({ ...prevConstraints, [property]: value }));
                        },
                        data: constraints,
                      })}
                      <VSpacer small />
                    </>
                  );
                  break;
                case `select`:
                  elements.push(
                    <>
                      <VSpacer small />
                      <Select
                        key={`select${id}`}
                        disableUnderline
                        className={classes.select}
                        onChange={async (evt) => {
                          const { value } = evt.target;
                          if (value === undefined || value === `noselect`) {
                            setConstraints((prevConstraints) => ({
                              ...prevConstraints,
                              [id]: null,
                            }));
                          } else {
                            setConstraints((prevConstraints) => ({
                              ...prevConstraints,
                              [id]: value,
                            }));
                          }
                        }}
                        value={constraints[id] || `noselect`}
                      >
                        <MenuItem value="noselect" key={`${id}menuItemnoselect`}>
                          {field.noTextId ? (
                            <Text style={{ cursor: `pointer` }} intlId={`${field.noTextId}`} />
                          ) : field.noText ? (
                            `${field.noText}`
                          ) : (
                            `-`
                          )}
                        </MenuItem>
                        {field.options.map((option: any) => (
                          <MenuItem value={`${option.value}`} key={`${id}menuItem${option.value}`}>
                            {option.label_id ? (
                              <Text style={{ cursor: `pointer` }} intlId={`${option.label_id}`} />
                            ) : option.text ? (
                              option.text
                            ) : option.label ? (
                              option.label
                            ) : (
                              ``
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                      <VSpacer small />
                    </>
                  );
                  break;
                case `minutes`:
                  elements.push(
                    <>
                      <VSpacer small />
                      {renderInputText({
                        variant: `outlined`,
                        property: id,
                        type: `number`,
                        handleUpdate: (property, value) => {
                          setConstraints((prevConstraints) => ({
                            ...prevConstraints,
                            [property]: value ? value * 60 : null,
                          }));
                        },
                        data: constraints,
                        suffix: `min`,
                        computed: (value) => (value ? value / 60 : undefined),
                      })}
                      <VSpacer small />
                    </>
                  );
                  break;
                case `kilometers`:
                  elements.push(
                    <>
                      <VSpacer small />
                      {renderInputText({
                        variant: `outlined`,
                        property: id,
                        type: `number`,
                        handleUpdate: (property, value) => {
                          setConstraints((prevConstraints) => ({
                            ...prevConstraints,
                            [property]: value ? value * 1000 : null,
                          }));
                        },
                        data: constraints,
                        suffix: `km`,
                        computed: (value) => (value ? value / 1000 : undefined),
                      })}
                      <VSpacer small />
                    </>
                  );
                  break;
                case `boolean`:
                  elements.push(
                    <>
                      <VSpacer small />
                      {/* TODO: TURN INTO A SWITCH */}
                      {renderCheckbox(
                        `${propertiesPrefixType}.${id}`,
                        id,
                        (property: string, value: boolean) => {
                          setConstraints((prevConstraints) => ({
                            ...prevConstraints,
                            [property]: value,
                          }));
                        },
                        constraints
                      )}
                      <VSpacer small />
                    </>
                  );
                  break;
                case `time_windows`:
                  elements.push(
                    <>
                      <VSpacer small />
                      <FormTimeWindows
                        key={`${field.type}${id}`}
                        timeWindows={constraints[id]}
                        onTimewindowsChange={(value: any) =>
                          setConstraints((prevConstraints) => ({ ...prevConstraints, [id]: value }))
                        }
                      />
                      <VSpacer small />
                    </>
                  );
                  break;
                case `time_window`:
                  elements.push(
                    <>
                      <VSpacer small />
                      <FormTimeWindows
                        key={`${field.type}${id}`}
                        timeWindows={constraints[id]}
                        onTimewindowsChange={(value: any) => {
                          setConstraints((prevConstraints) => ({ ...prevConstraints, [id]: value }));
                        }}
                        single
                      />
                      <VSpacer small />
                    </>
                  );
                  break;
                case `text_array`:
                  elements.push(
                    <>
                      <VSpacer small />
                      <FormArray
                        key={`${field.type}${id}`}
                        array={constraints[id]}
                        onUpdate={(value: any) => {
                          setConstraints((prevConstraints) => ({ ...prevConstraints, [id]: value }));
                        }}
                      />
                      <VSpacer small />
                    </>
                  );
                  break;
                case `location`:
                  elements.push(
                    <>
                      <VSpacer small />
                      {renderInputLocation({
                        prefix: propertiesPrefixType,
                        property: id,
                        handleUpdate: (property: string, location?: Location | null) => {
                          setConstraints((prevConstraints) => ({
                            ...prevConstraints,
                            [property]: location ? location.getValue() : undefined,
                          }));
                        },
                        data: constraints,
                      })}
                      <VSpacer small />
                    </>
                  );
                  break;
                case `geo_fences`:
                  if (typeData === `drivers`) {
                    elements.push(
                      <>
                        <VSpacer small />
                        <GeoFencesSelector
                          selectButtonStyles={{
                            backgroundColor: `transparent`,
                            border: `1px solid ${RoutalPalette.neutral40}`,
                            borderRadius: `4px`,
                            color: RoutalPalette.secondary40,
                            width: `calc(100% - 26px)`,
                          }}
                          geoFences={projectGeoFences ?? []}
                          selectedGeoFenceIds={constraints[`geo_fences`] ?? []}
                          handleUpdate={(geoFences: string[]) => {
                            setConstraints((prevConstraints) => ({
                              ...prevConstraints,
                              geo_fences: geoFences,
                            }));
                          }}
                        />
                      </>
                    );
                  }
                  break;
                default:
                  elements.push(
                    <>
                      <VSpacer small />
                      {renderInputText({
                        variant: `outlined`,
                        property: id,
                        handleUpdate: (property, value) => {
                          setConstraints((prevConstraints) => ({ ...prevConstraints, [property]: value }));
                        },
                        data: constraints,
                      })}
                      <VSpacer small />
                    </>
                  );
                  break;
              }
            }

            return <>{[...elements, <VSpacer small />].map((el) => el)}</>;
          })}
        </Grid>
      </RoutalDialog>
    );
  }
);
